<script lang="ts">
	import { goto, invalidate, onNavigate } from '$app/navigation';
	import { page } from '$app/state';
	import { Tooltip } from 'bits-ui';

	import SideNav from '$lib/components/SideNav.svelte';
	import { AUTH, logout } from '$lib/domain/auth';
	import '$lib/styles/tokens.css';
	import '$lib/styles/base.css';

	let { children, data } = $props();

	const onLogin = $derived(page.url.pathname === '/login');

	async function signOut() {
		await logout();
		await invalidate(AUTH);
		await goto('/login');
	}

	// Cross-fade the content between pages. Feature-detected rather than assumed, so a
	// browser without the API simply navigates as it always did.
	onNavigate((navigation) => {
		if (!document.startViewTransition) return;

		// Filtering a list rewrites the query string without changing the page. Animating
		// that would put a flicker on every keystroke.
		if (navigation.from?.url.pathname === navigation.to?.url.pathname) return;

		return new Promise((resolve) => {
			document.startViewTransition(async () => {
				resolve();
				await navigation.complete;
			});
		});
	});
</script>

<!-- One provider for the app: Bits UI tooltips read their timing from it. -->
<Tooltip.Provider delayDuration={200}>
	<div class="shell" class:bare={onLogin}>
		{#if !onLogin}
			<SideNav pathname={page.url.pathname} email={data.user?.email} onSignOut={signOut} />
		{/if}

		<main class:bare={onLogin}>{@render children()}</main>
	</div>
</Tooltip.Provider>

<style>
	/* The rail sizes itself and the content takes the rest. It is a grid rather than a flex
	   row so the rail can be sticky and full-height without the content column inheriting
	   either. */
	.shell {
		display: grid;
		grid-template-columns: auto minmax(0, 1fr);
		min-height: 100vh;
	}

	/* Sign-in has no chrome at all, so there is no rail column to leave room for. */
	.shell.bare {
		grid-template-columns: minmax(0, 1fr);
	}

	main {
		max-width: 78rem;
		width: 100%;
		margin: 0 auto;
		padding: var(--space-xl) var(--space-lg);
		/* Only the content crossfades. Left unnamed, the root snapshot would include the
		   rail, which then flickers on every navigation. */
		view-transition-name: page;
	}

	/* The sign-in screen has no header and no chrome, so the flat canvas reads as an
	   unstyled page. Two soft washes of the brand colour give it a horizon without
	   putting anything on it that has to be read. */
	main.bare {
		display: grid;
		place-items: center;
		min-height: 100vh;
		/* Overriding the shell's own width: the wash has to reach the edges of the
		   viewport, or it ends in a visible seam wherever the content column stops. */
		max-width: none;
		padding: var(--space-lg);
		background:
			radial-gradient(
				60rem 40rem at 15% -10%,
				color-mix(in srgb, var(--color-primary) 14%, transparent),
				transparent 70%
			),
			radial-gradient(
				45rem 30rem at 100% 110%,
				color-mix(in srgb, var(--color-primary) 10%, transparent),
				transparent 70%
			);
	}

	@media (max-width: 46rem) {
		/* The rail becomes a fixed bottom bar here, which takes it out of the grid's flow
		   entirely. Left at two columns, `main` would then be placed in the leftover `auto`
		   track and size to its max-content instead of the viewport — which is a page that
		   scrolls sideways on a phone. */
		.shell {
			grid-template-columns: minmax(0, 1fr);
		}

		/* The bar floats over the page, so the last thing on a scrolled page would sit
		   underneath it without this. */
		main:not(.bare) {
			padding-bottom: calc(5rem + env(safe-area-inset-bottom));
		}
	}
</style>
